import React, { useState, useEffect } from 'react';
import { baseURL } from '../apiConfig';
import './SalesReturn.css';

const SalesReturn = () => {
  // Tab control: 'process' (Process Return) | 'history' (Return History)
  const [activeTab, setActiveTab] = useState('process');

  // Input state
  const [searchInvoice, setSearchInvoice] = useState('');
  const [selectedBillType, setSelectedBillType] = useState('Regular'); // 'Regular' | 'Estimated'
  const [loading, setLoading] = useState(false);

  // Modals state
  const [showTypeModal, setShowTypeModal] = useState(false);
  const [pendingInvoiceQuery, setPendingInvoiceQuery] = useState('');
  
  // Return Confirm Modal state
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [itemToReturn, setItemToReturn] = useState(null);
  const [processingReturn, setProcessingReturn] = useState(false);

  // Active Invoice Data
  const [invoiceData, setInvoiceData] = useState(null);
  const [revisedBillInfo, setRevisedBillInfo] = useState(null);

  // History Data
  const [historyList, setHistoryList] = useState([]);
  const [loadingHistory, setLoadingHistory] = useState(false);

  // Toast message
  const [toast, setToast] = useState(null);

  const showToast = (message, type = 'success') => {
    setToast({ message, type });
    setTimeout(() => {
      setToast(null);
    }, 4500);
  };

  // Step 1: User enters invoice number and clicks Search
  const handleInitiateSearch = (e) => {
    if (e) e.preventDefault();
    const cleanInv = searchInvoice.trim();
    if (!cleanInv) {
      showToast('Kripya Sales Invoice Number enter karein!', 'error');
      return;
    }
    // Prompt the user to confirm Regular vs Estimated
    setPendingInvoiceQuery(cleanInv);
    setShowTypeModal(true);
  };

  // Step 2: User selects Regular or Estimated in the popup/modal
  const handleSelectTypeAndFetch = async (type) => {
    setSelectedBillType(type);
    setShowTypeModal(false);
    fetchInvoiceData(pendingInvoiceQuery, type);
  };

  // Step 3: Fetch invoice from backend
  const fetchInvoiceData = async (invoiceNo, billType) => {
    setLoading(true);
    setInvoiceData(null);
    setRevisedBillInfo(null);
    try {
      const response = await fetch(`${baseURL}/api/sales-return/fetch-invoice/?invoice=${encodeURIComponent(invoiceNo)}&type=${encodeURIComponent(billType)}`);
      const data = await response.json();

      if (data.success && data.invoice) {
        setInvoiceData(data.invoice);
        // Check if any returned items already exist on this bill
        const hasReturned = data.invoice.items.some(item => item.isReturned);
        if (hasReturned) {
          setRevisedBillInfo({
            returnInvoiceNo: `R/${data.invoice.invoiceNumber}`,
            pdfUrl: `${baseURL}/api/sales-return/view-pdf/${data.invoice.invoiceNumber}/`
          });
        }
      } else {
        showToast(data.message || 'Invoice nahi mila!', 'error');
      }
    } catch (err) {
      showToast('Server se connect karne me dikkat aayi: ' + err.message, 'error');
    } finally {
      setLoading(false);
    }
  };

  // Step 4: Click on "X" button for an item
  const handleOpenReturnModal = (item) => {
    if (item.isReturned) {
      showToast('Ye item pehle se hi return ho chuka hai!', 'error');
      return;
    }
    setItemToReturn(item);
    setShowConfirmModal(true);
  };

  // Step 5: Confirm return on "Yes"
  const handleConfirmReturn = async () => {
    if (!itemToReturn || !invoiceData) return;

    setProcessingReturn(true);
    try {
      const loggedUser = sessionStorage.getItem('username') || 'Admin';
      const payload = {
        invoiceNumber: invoiceData.invoiceNumber,
        itemRowId: itemToReturn.id,
        tagId: itemToReturn.tagId,
        billType: invoiceData.billType,
        remarks: 'Customer Return processed via Sales Return page',
        returnedBy: loggedUser
      };

      const response = await fetch(`${baseURL}/api/sales-return/process-return/`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      });

      const resData = await response.json();

      if (resData.success) {
        showToast(resData.message, 'success');
        setShowConfirmModal(false);

        // Update local state: mark item as returned
        const updatedItems = invoiceData.items.map(it => {
          if (it.id === itemToReturn.id) {
            return { ...it, isReturned: true, status: 'Returned' };
          }
          return it;
        });

        const newGrandTotal = resData.new_grand_total;
        setInvoiceData(prev => ({
          ...prev,
          items: updatedItems,
          overallBillTotal: newGrandTotal,
          billSettlementAmt: newGrandTotal,
          customerCurrentCredit: Math.max(0, (prev.customerCurrentCredit || 0) - (resData.credit_adjusted || 0))
        }));

        setRevisedBillInfo({
          returnInvoiceNo: resData.return_invoice_no,
          pdfUrl: `${baseURL}${resData.pdf_url}`,
          newGrandTotal: newGrandTotal,
          creditAdjusted: resData.credit_adjusted
        });

        setItemToReturn(null);
      } else {
        showToast(resData.message || 'Return process fail ho gaya!', 'error');
      }
    } catch (err) {
      showToast('Error: ' + err.message, 'error');
    } finally {
      setProcessingReturn(false);
    }
  };

  // Fetch Return History
  const fetchReturnHistory = async () => {
    setLoadingHistory(true);
    try {
      const res = await fetch(`${baseURL}/api/sales-return/list/`);
      const data = await res.json();
      if (data.success) {
        setHistoryList(data.data || []);
      }
    } catch (err) {
      console.error(err);
    } finally {
      setLoadingHistory(false);
    }
  };

  useEffect(() => {
    if (activeTab === 'history') {
      fetchReturnHistory();
    }
  }, [activeTab]);

  return (
    <div className="sales-return-wrapper">
      <div className="sales-return-container">
        
        {/* Header Section */}
        <div className="sr-header-card">
          <div className="sr-title-section">
            <h2>🔄 Sales Return & Revised Invoicing</h2>
            <p>Sales invoice se stock wapas layein, customer credit reverse karein aur "R/" revised bill generate karein</p>
          </div>
          <div className="sr-nav-tabs">
            <button 
              className={`sr-tab-btn ${activeTab === 'process' ? 'active' : ''}`}
              onClick={() => setActiveTab('process')}
            >
              🔄 Process Return
            </button>
            <button 
              className={`sr-tab-btn ${activeTab === 'history' ? 'active' : ''}`}
              onClick={() => setActiveTab('history')}
            >
              📜 Return History
            </button>
          </div>
        </div>

        {activeTab === 'process' && (
          <>
            {/* Search Invoice Form */}
            <div className="sr-search-card">
              <form className="sr-search-form" onSubmit={handleInitiateSearch}>
                <div className="sr-form-group">
                  <label>Sales Invoice Number :</label>
                  <input
                    type="text"
                    className="sr-input"
                    placeholder="Enter Invoice No. (e.g. 101)"
                    value={searchInvoice}
                    onChange={(e) => setSearchInvoice(e.target.value)}
                  />
                </div>

                <div className="sr-form-group">
                  <label>Default Sale Type :</label>
                  <select 
                    className="sr-select"
                    value={selectedBillType}
                    onChange={(e) => setSelectedBillType(e.target.value)}
                  >
                    <option value="Regular">Regular Sales (Tax Invoice)</option>
                    <option value="Estimated">Estimated Sales (Kaccha Bill)</option>
                  </select>
                </div>

                <button 
                  type="submit" 
                  className="sr-btn-search"
                  disabled={loading}
                >
                  {loading ? '🔍 Fetching...' : '🔍 Load Invoice Items'}
                </button>
              </form>
            </div>

            {/* Invoice Header Details */}
            {invoiceData && (
              <div className="sr-invoice-banner">
                <div className="sr-info-item">
                  <span className="sr-info-label">Invoice Number</span>
                  <span className="sr-info-val gold">
                    #{invoiceData.invoiceNumber}
                  </span>
                </div>

                <div className="sr-info-item">
                  <span className="sr-info-label">Sale Type</span>
                  <span className={`sr-info-val badge ${invoiceData.billType === 'Regular' ? 'sr-badge-regular' : 'sr-badge-estimated'}`}>
                    {invoiceData.billType} Sales
                  </span>
                </div>

                <div className="sr-info-item">
                  <span className="sr-info-label">Customer Name</span>
                  <span className="sr-info-val">{invoiceData.customerName}</span>
                </div>

                <div className="sr-info-item">
                  <span className="sr-info-label">Contact / Mobile</span>
                  <span className="sr-info-val">{invoiceData.mobile || 'N/A'}</span>
                </div>

                <div className="sr-info-item">
                  <span className="sr-info-label">Payment Mode</span>
                  <span className="sr-info-val" style={{ textTransform: 'uppercase' }}>
                    {invoiceData.paymentMode}
                  </span>
                </div>

                <div className="sr-info-item">
                  <span className="sr-info-label">Bill Date</span>
                  <span className="sr-info-val">{invoiceData.billDate || 'N/A'}</span>
                </div>

                <div className="sr-info-item">
                  <span className="sr-info-label">Customer Credit (Udhaar)</span>
                  <span className="sr-info-val" style={{ color: invoiceData.customerCurrentCredit > 0 ? '#ffb703' : '#999' }}>
                    ₹{Number(invoiceData.customerCurrentCredit || 0).toLocaleString()}
                  </span>
                </div>

                <div className="sr-info-item">
                  <span className="sr-info-label">Current Bill Total</span>
                  <span className="sr-info-val gold">
                    ₹{Number(invoiceData.overallBillTotal || 0).toLocaleString()}
                  </span>
                </div>
              </div>
            )}

            {/* Items Table */}
            {invoiceData && invoiceData.items && (
              <div className="sr-table-card">
                <div className="sr-table-title">
                  <h3>Items in Invoice #{invoiceData.invoiceNumber}</h3>
                  <span style={{ fontSize: '12px', color: '#aaa' }}>
                    Return karne ke liye kisi bhi item ke aage <strong>'❌ Return'</strong> par click karein
                  </span>
                </div>

                <table className="sr-table">
                  <thead>
                    <tr>
                      <th style={{ width: '4%' }}>#</th>
                      <th style={{ width: '6%' }}>Photo</th>
                      <th style={{ width: '12%' }}>Tag / Item ID</th>
                      <th style={{ width: '18%' }}>Item Name</th>
                      <th style={{ width: '8%' }}>Metal & Purity</th>
                      <th style={{ width: '8%' }}>Weight</th>
                      <th style={{ width: '8%' }}>Rate/1g</th>
                      <th style={{ width: '8%' }}>Making</th>
                      <th style={{ width: '8%' }}>Sub Total</th>
                      <th style={{ width: '8%' }}>Grand Total</th>
                      <th style={{ width: '10%' }}>Status</th>
                      <th style={{ width: '10%', textAlign: 'center' }}>Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {invoiceData.items.map((item, index) => {
                      const isRet = item.isReturned;
                      return (
                        <tr key={item.id} className={isRet ? 'sr-row-returned' : ''}>
                          <td>{index + 1}</td>
                          <td>
                            {item.imageUrl ? (
                              <img src={item.imageUrl} alt="Item" className="sr-thumb-img" />
                            ) : (
                              <div style={{ width: 44, height: 44, background: '#292929', borderRadius: 4, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 18 }}>
                                💎
                              </div>
                            )}
                          </td>
                          <td><strong>[{item.tagId || 'N/A'}]</strong></td>
                          <td>{item.itemName}</td>
                          <td>{item.metal} {item.purity}</td>
                          <td>{item.weight}g</td>
                          <td>₹{Number(item.ratePerGram || 0).toLocaleString()}</td>
                          <td>₹{Number(item.makingCharges || 0).toLocaleString()}</td>
                          <td>₹{Number(item.itemTotal || 0).toLocaleString()}</td>
                          <td><strong>₹{Number(item.grandTotal || item.itemTotal || 0).toLocaleString()}</strong></td>
                          <td>
                            {isRet ? (
                              <span className="sr-badge-returned">❌ RETURNED</span>
                            ) : (
                              <span className="sr-badge-active">✅ Active</span>
                            )}
                          </td>
                          <td style={{ textAlign: 'center' }}>
                            {isRet ? (
                              <span style={{ color: '#e57373', fontSize: '11px', fontWeight: 'bold' }} className="no-strike">
                                Restored
                              </span>
                            ) : (
                              <button
                                className="sr-btn-return"
                                onClick={() => handleOpenReturnModal(item)}
                                title="Return this item to stock"
                              >
                                ❌ Return
                              </button>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}

            {/* Revised Bill Banner & Actions */}
            {revisedBillInfo && (
              <div className="sr-totals-card">
                <div className="sr-totals-breakdown">
                  <div className="sr-total-box">
                    <span className="sr-total-label">Revised Bill Number</span>
                    <span className="sr-total-num red">{revisedBillInfo.returnInvoiceNo}</span>
                  </div>

                  <div className="sr-total-box">
                    <span className="sr-total-label">Adjusted Grand Total</span>
                    <span className="sr-total-num gold">
                      ₹{Number(invoiceData ? invoiceData.overallBillTotal : revisedBillInfo.newGrandTotal || 0).toLocaleString()}
                    </span>
                  </div>

                  {revisedBillInfo.creditAdjusted > 0 && (
                    <div className="sr-total-box">
                      <span className="sr-total-label">Customer Credit Reversed</span>
                      <span className="sr-total-num" style={{ color: '#4caf50' }}>
                        - ₹{Number(revisedBillInfo.creditAdjusted).toLocaleString()}
                      </span>
                    </div>
                  )}
                </div>

                <div className="sr-actions-group">
                  <button 
                    className="sr-btn-view-pdf"
                    onClick={() => window.open(revisedBillInfo.pdfUrl, '_blank')}
                  >
                    🖨️ View / Print Revised Bill ({revisedBillInfo.returnInvoiceNo})
                  </button>
                </div>
              </div>
            )}
          </>
        )}

        {/* Tab 2: History Register */}
        {activeTab === 'history' && (
          <div className="sr-table-card">
            <div className="sr-table-title">
              <h3>Sales Return History Register</h3>
              <button 
                className="sr-tab-btn" 
                onClick={fetchReturnHistory}
                style={{ fontSize: '12px' }}
              >
                🔄 Refresh
              </button>
            </div>

            {loadingHistory ? (
              <p style={{ color: '#aaa', padding: 20 }}>Loading history...</p>
            ) : historyList.length === 0 ? (
              <p style={{ color: '#777', padding: 20 }}>Abhi tak koi Sales Return record nahi mila.</p>
            ) : (
              <table className="sr-history-table">
                <thead>
                  <tr>
                    <th>Return No</th>
                    <th>Date</th>
                    <th>Orig. Inv</th>
                    <th>Type</th>
                    <th>Customer</th>
                    <th>Tag ID</th>
                    <th>Item Name</th>
                    <th>Item Cost</th>
                    <th>Credit Reversed</th>
                    <th>Revised Total</th>
                    <th>Action</th>
                  </tr>
                </thead>
                <tbody>
                  {historyList.map(rec => (
                    <tr key={rec.id}>
                      <td><strong style={{ color: '#ef5350' }}>{rec.returnInvoiceNo}</strong></td>
                      <td>{rec.returnDate}</td>
                      <td>#{rec.originalInvoiceNo}</td>
                      <td>{rec.saleType}</td>
                      <td>{rec.customerName}</td>
                      <td><strong>[{rec.tagId}]</strong></td>
                      <td>{rec.itemName}</td>
                      <td>₹{Number(rec.itemTotal || 0).toLocaleString()}</td>
                      <td style={{ color: rec.creditAdjusted > 0 ? '#4caf50' : '#888' }}>
                        ₹{Number(rec.creditAdjusted || 0).toLocaleString()}
                      </td>
                      <td><strong>₹{Number(rec.newBillTotal || 0).toLocaleString()}</strong></td>
                      <td>
                        <button
                          className="sr-btn-view-pdf"
                          style={{ padding: '4px 10px', fontSize: '12px' }}
                          onClick={() => window.open(`${baseURL}/api/sales-return/view-pdf/${rec.originalInvoiceNo}/`, '_blank')}
                        >
                          🖨️ PDF
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        )}

        {/* POPUP 1: SALE TYPE CONFIRMATION MODAL */}
        {showTypeModal && (
          <div className="sr-modal-backdrop" onClick={() => setShowTypeModal(false)}>
            <div className="sr-modal-content" onClick={(e) => e.stopPropagation()}>
              <div className="sr-modal-header">
                <h3>🏷️ Confirm Sale Type</h3>
              </div>
              <div className="sr-modal-body">
                <p>
                  Aap <strong>Invoice #{pendingInvoiceQuery}</strong> check kar rahe hain. Kripya confirm karein ki ye kaun sa bill hai:
                </p>

                <div className="sr-type-choices">
                  <div 
                    className="sr-type-choice-btn"
                    onClick={() => handleSelectTypeAndFetch('Regular')}
                  >
                    <h4>💎 Regular Sales</h4>
                    <p>Pakka Bill / Tax Invoice (sales table)</p>
                  </div>

                  <div 
                    className="sr-type-choice-btn"
                    onClick={() => handleSelectTypeAndFetch('Estimated')}
                  >
                    <h4>📝 Estimated Sales</h4>
                    <p>Kaccha Bill / Estimation (Estimated_sales table)</p>
                  </div>
                </div>
              </div>
              <div className="sr-modal-actions">
                <button 
                  className="sr-btn-cancel" 
                  onClick={() => setShowTypeModal(false)}
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        )}

        {/* POPUP 2: RETURN CONFIRMATION MODAL */}
        {showConfirmModal && itemToReturn && (
          <div className="sr-modal-backdrop" onClick={() => !processingReturn && setShowConfirmModal(false)}>
            <div className="sr-modal-content" onClick={(e) => e.stopPropagation()}>
              <div className="sr-modal-header">
                <h3>⚠️ Return Stock Confirmation</h3>
              </div>
              <div className="sr-modal-body">
                <div className="sr-alert-box">
                  <strong>Are you Sure, you want to bring back this stock ?</strong>
                </div>

                <div style={{ background: '#262626', padding: '12px', borderRadius: '6px', margin: '12px 0' }}>
                  <p style={{ margin: '4px 0' }}><strong>Item Name:</strong> {itemToReturn.itemName}</p>
                  <p style={{ margin: '4px 0' }}><strong>Tag ID:</strong> [{itemToReturn.tagId}]</p>
                  <p style={{ margin: '4px 0' }}><strong>Weight:</strong> {itemToReturn.weight}g | <strong>Purity:</strong> {itemToReturn.purity}</p>
                  <p style={{ margin: '4px 0', color: '#d4af37' }}>
                    <strong>Item Amount:</strong> ₹{Number(itemToReturn.grandTotal || itemToReturn.itemTotal || 0).toLocaleString()}
                  </p>
                </div>

                <ul style={{ fontSize: '13px', color: '#bbb', paddingLeft: '20px', lineHeight: '1.6' }}>
                  <li>Stock table me iska status wapas <strong>"Available"</strong> ho jayega.</li>
                  {invoiceData && invoiceData.paymentMode && invoiceData.paymentMode.toLowerCase().includes('credit') ? (
                    <li style={{ color: '#81c784' }}>
                      Yeh item Credit (Udhaar) par tha, isliye customer ke account se ₹{Number(itemToReturn.grandTotal || itemToReturn.itemTotal || 0).toLocaleString()} credit wapas ho jayega.
                    </li>
                  ) : (
                    <li>Payment mode: {invoiceData ? invoiceData.paymentMode : 'N/A'}.</li>
                  )}
                  <li>Revised Bill <strong>"R/{invoiceData ? invoiceData.invoiceNumber : ''}"</strong> generate hoga jisme is item par red line se cross hoga.</li>
                </ul>
              </div>
              <div className="sr-modal-actions">
                <button 
                  className="sr-btn-cancel"
                  disabled={processingReturn}
                  onClick={() => setShowConfirmModal(false)}
                >
                  No, Cancel
                </button>
                <button 
                  className="sr-btn-confirm"
                  disabled={processingReturn}
                  onClick={handleConfirmReturn}
                >
                  {processingReturn ? 'Processing...' : 'Yes, Return Stock'}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* TOAST MESSAGE */}
        {toast && (
          <div className={`sr-toast ${toast.type}`}>
            {toast.message}
          </div>
        )}

      </div>
    </div>
  );
};

export default SalesReturn;
